/**
 * Shared UI primitives.
 *
 * Includes the hardware illustration generator: rather than hot-linking
 * copyrighted product photography (spec 97), each profile gets a deterministic
 * vector illustration derived from its own properties -- a tube amplifier draws
 * tubes, a ported speaker draws a port, a rack DAC draws a display. The
 * `image` field on every profile can later point at a licensed photograph
 * without any other change.
 */

import { memo, useMemo } from 'react';
import type { AnyProfile } from '../../types/hardware';
import type { Confidence } from '../../types/provenance';
import { CONFIDENCE_COLOR, CONFIDENCE_LABEL, summarizeConfidence } from '../../types/provenance';

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

export const fmtDb = (v: number | null | undefined, digits = 1): string =>
  v === null || v === undefined || !Number.isFinite(v) ? '--' : `${v > 0 ? '+' : ''}${v.toFixed(digits)}`;

export const fmtTime = (sec: number): string => {
  if (!Number.isFinite(sec) || sec < 0) sec = 0;
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  const ms = Math.floor((sec % 1) * 1000);
  return `${m}:${String(s).padStart(2, '0')}.${String(ms).padStart(3, '0')}`;
};

export const fmtHz = (hz: number): string =>
  hz >= 1000 ? `${(hz / 1000).toFixed(hz >= 10000 ? 0 : 1)} kHz` : `${hz.toFixed(hz < 10 ? 1 : 0)} Hz`;

export const fmtBytes = (n: number): string => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} kB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

const CURRENCY: Record<string, string> = { USD: '$', EUR: '€', GBP: '£', JPY: '¥' };

export const fmtPrice = (p: AnyProfile['price']): string => {
  const sym = CURRENCY[p.currency] ?? '';
  const n = p.amount >= 10000 ? p.amount.toLocaleString('en-US') : String(p.amount);
  return `${sym}${n}`;
};

export const PRICE_STATUS_LABEL: Record<string, string> = {
  MSRP: 'MSRP',
  ORIGINAL_RETAIL: 'orig. retail',
  CURRENT_RETAIL: 'current retail',
  APPROXIMATE: 'approximate',
  DISCONTINUED_USED: 'used market',
  UNKNOWN: 'unknown',
};

// ---------------------------------------------------------------------------
// Confidence
// ---------------------------------------------------------------------------

export function ConfidenceBadge({ value, label }: { value: Confidence; label?: boolean }) {
  return (
    <span className="conf" style={{ color: CONFIDENCE_COLOR[value] }} title={`Profile confidence: ${CONFIDENCE_LABEL[value]}`}>
      <span className="dot" style={{ background: CONFIDENCE_COLOR[value] }} />
      {label !== false && CONFIDENCE_LABEL[value]}
    </span>
  );
}

/** Stacked bar showing the mix of confidence tags across a whole profile. */
export function ConfidenceBar({ profile }: { profile: AnyProfile }) {
  const summary = useMemo(() => summarizeConfidence(profile), [profile]);
  if (!summary.total) return null;
  const order: Confidence[] = ['MEASURED', 'MANUFACTURER_SPEC', 'DERIVED', 'ESTIMATED', 'MODELLED', 'UNKNOWN'];
  return (
    <div
      title={order
        .filter((c) => summary.counts[c])
        .map((c) => `${CONFIDENCE_LABEL[c]}: ${summary.counts[c]}`)
        .join('  |  ')}
    >
      <div className="conf-bar">
        {order.map((c) => {
          const n = summary.counts[c] ?? 0;
          if (!n) return null;
          return <span key={c} style={{ width: `${(n / summary.total) * 100}%`, background: CONFIDENCE_COLOR[c] }} />;
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Layout helpers
// ---------------------------------------------------------------------------

export function Stat({
  label,
  value,
  sub,
  tone,
  title,
}: {
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  tone?: 'warn' | 'danger' | 'ok';
  title?: string;
}) {
  return (
    <div className={`stat${tone ? ` ${tone}` : ''}`} title={title}>
      <label>{label}</label>
      <b>{value}</b>
      {sub !== undefined && <small>{sub}</small>}
    </div>
  );
}

export function Field({
  label,
  value,
  children,
}: {
  label: string;
  value?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="field">
      <label>{label}</label>
      {children}
      {value !== undefined && <span className="val">{value}</span>}
    </div>
  );
}

export function Slider({
  label,
  min,
  max,
  step,
  value,
  onChange,
  format,
  disabled,
}: {
  label: string;
  min: number;
  max: number;
  step: number;
  value: number;
  onChange: (v: number) => void;
  format?: (v: number) => string;
  disabled?: boolean;
}) {
  return (
    <Field label={label} value={format ? format(value) : value.toFixed(2)}>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(Number(e.target.value))}
        aria-label={label}
      />
    </Field>
  );
}

export function Segmented<T extends string | number>({
  options,
  value,
  onChange,
  small,
  ariaLabel,
}: {
  options: Array<{ value: T; label: string; title?: string }>;
  value: T;
  onChange: (v: T) => void;
  small?: boolean;
  ariaLabel?: string;
}) {
  return (
    <div className={`seg${small ? ' sm' : ''}`} role="group" aria-label={ariaLabel}>
      {options.map((o) => (
        <button
          key={String(o.value)}
          type="button"
          title={o.title}
          aria-pressed={o.value === value}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Toggle({
  label,
  checked,
  onChange,
  title,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  title?: string;
}) {
  return (
    <label className="check" title={title}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span>{label}</span>
    </label>
  );
}

export function SectionTitle({ children }: { children: React.ReactNode }) {
  return <h4 className="section-title">{children}</h4>;
}

// ---------------------------------------------------------------------------
// Hardware illustration
// ---------------------------------------------------------------------------

function hash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function rng(seed: number) {
  let s = seed || 1;
  return () => {
    s ^= s << 13; s >>>= 0;
    s ^= s >> 17;
    s ^= s << 5; s >>>= 0;
    return s / 4294967296;
  };
}

/**
 * Deterministic vector illustration.
 *
 * Reads real properties -- tube complement, driver count, enclosure type,
 * balanced outputs -- so the picture is informative rather than decorative:
 * a three-way ported floorstander looks different from a coaxial standmount
 * because it IS different.
 */
export const HardwareArt = memo(function HardwareArt({
  profile,
  height = 44,
}: {
  profile: AnyProfile;
  height?: number;
}) {
  const accent = profile.image?.accent ?? '#5a6a7a';
  const seed = hash(profile.image?.seed ?? profile.id);
  const r = rng(seed);
  const W = 200;
  const H = 60;

  const body: React.ReactNode[] = [];

  if (profile.category === 'dac' || profile.category === 'amp') {
    const isTube =
      profile.category === 'amp'
        ? profile.topology.startsWith('tube') || profile.topology === 'hybrid'
        : profile.analog.stage === 'tube';
    const isMono = profile.category === 'amp' && profile.form === 'monoblock';
    const w = isMono ? 96 : 176;
    const x = (W - w) / 2;
    body.push(<rect key="ch" x={x} y={14} width={w} height={34} rx={2} fill="#151a21" stroke="#242c36" />);
    body.push(<rect key="fp" x={x} y={14} width={w} height={5} fill="#1b222b" />);

    if (isTube) {
      const n = profile.category === 'amp' && profile.topology === 'tube-set' ? 2 : 4;
      for (let i = 0; i < n; i++) {
        const cx = x + (w * (i + 1)) / (n + 1);
        body.push(
          <g key={`t${i}`}>
            <rect x={cx - 5} y={4} width={10} height={16} rx={5} fill="#241a10" stroke={accent} strokeWidth={0.7} opacity={0.9} />
            <ellipse cx={cx} cy={13} rx={3} ry={4} fill={accent} opacity={0.5} />
          </g>,
        );
      }
    } else if (profile.category === 'amp') {
      // Heatsink fins scale with idle dissipation: Class A gets more.
      const fins = profile.topology === 'solid-state-a' ? 14 : profile.topology === 'class-d' ? 4 : 9;
      for (let i = 0; i < fins; i++) {
        body.push(<rect key={`f${i}`} x={x + 4 + i * ((w - 8) / fins)} y={22} width={1.6} height={20} fill="#2a323d" />);
      }
    } else {
      body.push(<rect key="disp" x={x + 10} y={24} width={54} height={14} rx={1} fill="#06120f" stroke="#1d2a28" />);
      for (let i = 0; i < 5; i++) {
        body.push(<rect key={`s${i}`} x={x + 14 + i * 9} y={28} width={5} height={6} fill={accent} opacity={0.25 + r() * 0.6} />);
      }
    }

    const knobs = profile.category === 'amp' ? 2 : 1;
    for (let i = 0; i < knobs; i++) {
      const cx = x + w - 18 - i * 22;
      body.push(<circle key={`k${i}`} cx={cx} cy={31} r={7} fill="#1c232c" stroke="#333d49" />);
      body.push(<line key={`kl${i}`} x1={cx} y1={31} x2={cx} y2={25} stroke={accent} strokeWidth={1.4} />);
    }
    if (isMono) {
      body.push(<rect key="ch2" x={x + w + 8} y={14} width={0} height={0} fill="none" />);
    }
    body.push(<rect key="feet" x={x + 8} y={48} width={10} height={3} fill="#1b222b" />);
    body.push(<rect key="feet2" x={x + w - 18} y={48} width={10} height={3} fill="#1b222b" />);
  } else if (profile.category === 'speaker') {
    const sp = profile;
    const tall = sp.speakerType === 'floorstander' || sp.speakerType === 'electrostatic' || sp.speakerType === 'line-source';
    const panel = sp.speakerType === 'electrostatic' || sp.speakerType === 'planar-magnetic' || sp.cabinet.enclosure === 'dipole-panel';
    const w = panel ? 64 : tall ? 44 : 56;
    const h = tall || panel ? 52 : 38;
    const x = (W - w) / 2;
    const y = H - h - 4;

    if (panel) {
      body.push(<rect key="p" x={x} y={y} width={w} height={h} rx={2} fill="#0e1319" stroke={accent} strokeWidth={0.8} opacity={0.9} />);
      for (let i = 0; i < 9; i++) {
        body.push(<line key={`w${i}`} x1={x + 4 + i * ((w - 8) / 8)} y1={y + 4} x2={x + 4 + i * ((w - 8) / 8)} y2={y + h - 4} stroke={accent} strokeWidth={0.4} opacity={0.35} />);
      }
    } else if (sp.speakerType === 'horn') {
      body.push(<rect key="c" x={x} y={y} width={w} height={h} rx={2} fill="#161b22" stroke="#252d38" />);
      body.push(<path key="horn" d={`M ${x + 10} ${y + 10} L ${x + w - 10} ${y + 4} L ${x + w - 10} ${y + 24} L ${x + 10} ${y + 18} Z`} fill="#1e2530" stroke={accent} strokeWidth={0.7} opacity={0.85} />);
      body.push(<circle key="w1" cx={x + w / 2} cy={y + h - 12} r={9} fill="#0f141a" stroke="#2a323d" />);
    } else {
      body.push(<rect key="c" x={x} y={y} width={w} height={h} rx={2} fill="#161b22" stroke="#252d38" />);
      const drivers = Math.max(2, Math.min(4, sp.driverCount ?? (sp.wayCount ?? 2) + 1));
      const isCoax = sp.speakerType === 'coaxial' || sp.drivers.includes('coaxial-point-source');
      for (let i = 0; i < drivers; i++) {
        const cy = y + 9 + i * ((h - 16) / Math.max(1, drivers - 1));
        const rad = i === 0 ? 4 : 7 - Math.min(2, i);
        body.push(<circle key={`d${i}`} cx={x + w / 2} cy={cy} r={rad} fill="#0f141a" stroke="#2f3844" />);
        if (i === 0 || isCoax) {
          body.push(<circle key={`dc${i}`} cx={x + w / 2} cy={cy} r={rad * 0.42} fill={accent} opacity={0.55} />);
        }
      }
      if (sp.cabinet.enclosure === 'ported' || sp.cabinet.enclosure === 'passive-radiator') {
        body.push(<ellipse key="port" cx={x + w / 2} cy={y + h - 6} rx={7} ry={2.6} fill="#05080b" stroke="#212831" />);
      }
    }
  } else {
    // Headphone
    const cx = W / 2;
    body.push(<path key="band" d={`M ${cx - 34} 32 A 34 26 0 0 1 ${cx + 34} 32`} fill="none" stroke="#2b333e" strokeWidth={3.2} />);
    for (const side of [-1, 1]) {
      const x = cx + side * 34;
      body.push(<ellipse key={`cup${side}`} cx={x} cy={38} rx={11} ry={14} fill="#141a21" stroke="#2b333e" />);
      body.push(<ellipse key={`pad${side}`} cx={x} cy={38} rx={7} ry={10} fill="#0c1015" stroke={accent} strokeWidth={0.7} opacity={0.85} />);
      if (profile.category === 'headphone' && profile.headphoneType === 'electrostatic') {
        for (let i = 0; i < 5; i++) {
          body.push(<line key={`g${side}${i}`} x1={x - 5} y1={32 + i * 3} x2={x + 5} y2={32 + i * 3} stroke={accent} strokeWidth={0.4} opacity={0.5} />);
        }
      }
    }
  }

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={height} preserveAspectRatio="xMidYMid meet" role="img" aria-label={`${profile.manufacturer} ${profile.model} illustration`}>
      <rect width={W} height={H} fill="#0a0d11" />
      {body}
    </svg>
  );
});

// ---------------------------------------------------------------------------
// Misc
// ---------------------------------------------------------------------------

export function Note({ children, tone }: { children: React.ReactNode; tone?: 'warn' | 'danger' | 'info' }) {
  return <div className={`note${tone ? ` ${tone}` : ''}`}>{children}</div>;
}

export function Spinner() {
  return <span className="spin" aria-label="Working" />;
}

export function Kbd({ children }: { children: React.ReactNode }) {
  return <span className="kbd">{children}</span>;
}
